import { msalInstance, acquireBearerToken } from "./auth";

export interface StreamChunk {
  type: "chunk" | "done" | "error" | "auth_ok" | "pong" | "status" | "tool_call_start" | "tool_call_chunk" | "tool_call_end" | "answer_chunk" | "upload_complete" | "upload_partial" | "upload_failed";
  id?: string;
  agent?: string;
  chunk?: string;
  content?: string;
  tool_call_id?: string;
  tool?: string;
  args?: Record<string, unknown>;
  error?: string;
  response?: Record<string, unknown>;
  detail?: string;
  conversation_id?: string;
  upload_session_id?: string;
}

export interface ChatWebSocket {
  socket: WebSocket;
  sendChat: (payload: Record<string, unknown>) => void;
  cancel: () => void;
  close: () => void;
}

const KEEPALIVE_INTERVAL_MS = 30_000;

export class WebSocketAuthError extends Error {
  constructor(message = 'Unable to acquire authentication token for WebSocket connection') {
    super(message);
    this.name = 'WebSocketAuthError';
  }
}

export async function createWebSocketConnection(): Promise<{ socket: WebSocket; group: string } | null> {
  const bearerToken = await acquireBearerToken(msalInstance, { forceRefresh: true });
  if (!bearerToken) {
    throw new WebSocketAuthError();
  }

  const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
  const wsUrl = baseUrl.replace(/^http/, "ws") + "/ws/chat";

  try {
    const socket = new WebSocket(wsUrl);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        socket.close();
        reject(new Error("WebSocket connection timeout"));
      }, 10_000);

      socket.onopen = () => {
        clearTimeout(timeout);
        console.log("WebSocket connected to", wsUrl);

        // Send auth message
        socket.send(JSON.stringify({ type: "auth", token: bearerToken }));
      };

      socket.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data) as StreamChunk;
          if (msg.type === "auth_ok") {
            // Start keepalive ping
            const pingInterval = setInterval(() => {
              if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: "ping" }));
              } else {
                clearInterval(pingInterval);
              }
            }, KEEPALIVE_INTERVAL_MS);

            // Store interval for cleanup
            (socket as any)._pingInterval = pingInterval;

            // Generate a group ID for compatibility with existing streamingStore
            const group = crypto.randomUUID();
            resolve({ socket, group });
          }
        } catch {
          // Non-JSON message or parse error during auth phase
        }
      };

      socket.onerror = (error) => {
        clearTimeout(timeout);
        const intentional = (socket as any)._intentionalClose === true;
        if (!intentional) {
          console.error("WebSocket error:", error);
        }
        reject(error);
      };

      socket.onclose = () => {
        clearTimeout(timeout);
        const pingInterval = (socket as any)._pingInterval;
        if (pingInterval) clearInterval(pingInterval);
      };
    });
  } catch (error) {
    console.error("Error creating WebSocket connection:", error);
    return null;
  }
}

export function sendChatOverWs(socket: WebSocket, payload: Record<string, unknown>): void {
  if (socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "chat", payload }));
  } else {
    console.error("WebSocket not open, cannot send chat");
  }
}

export function cancelChatOverWs(socket: WebSocket): void {
  if (socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "cancel" }));
  }
}

export function parseStreamMessage(data: string): StreamChunk {
  return JSON.parse(data);
}
